import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Divider,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  IconButton
} from '@mui/material';
import {
  ViewModule as FeaturesIcon,
  ExpandMore as ExpandMoreIcon,
  Star as PriorityIcon,
  Check as DoneIcon,
  Assignment as TaskIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FlagOutlined as FlagIcon
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import DocumentTypeTemplate from '../DocumentTypeTemplate';
import useFeature from '../../../hooks/useFeature';
import useNotification from '../../../hooks/useNotification';

const Features = ({ projectId, existingContent = '', onSave }) => {
  const [features, setFeatures] = useState([]);
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editedFeature, setEditedFeature] = useState(null);
  
  const { getFeaturesByProject, createFeature, updateFeature, deleteFeature } = useFeature();
  const { showNotification } = useNotification();
  
  // Load features if project ID is provided
  useEffect(() => {
    if (projectId) {
      const loadFeatures = async () => {
        try {
          const projectFeatures = await getFeaturesByProject(projectId);
          setFeatures(projectFeatures || []);
        } catch (error) {
          console.error('Error loading features:', error);
        }
      };
      
      loadFeatures();
    }
  }, [projectId, getFeaturesByProject]);
  
  // Load features from existing content if available
  useEffect(() => {
    if (existingContent) {
      try {
        // Try to parse as JSON first
        const featuresData = JSON.parse(existingContent);
        if (Array.isArray(featuresData)) {
          setFeatures(featuresData);
        }
      } catch (e) {
        // If not JSON, try to parse from markdown
        // This is a simplified example
        console.log('Parsing features from markdown not implemented');
      }
    }
  }, [existingContent]);
  
  const renderFeaturesPreview = (content) => {
    // Try to parse as JSON first
    try {
      const featuresData = JSON.parse(content);
      if (Array.isArray(featuresData)) {
        return (
          <Box>
            {featuresData.map((feature, index) => (
              <Accordion key={feature._id || index} defaultExpanded={index === 0}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
                      {feature.title}
                    </Typography>
                    <Chip 
                      label={feature.priority} 
                      size="small"
                      color={
                        feature.priority === 'high' ? 'error' :
                        feature.priority === 'medium' ? 'warning' :
                        'default'
                      }
                      sx={{ mr: 1 }}
                    />
                    <Chip 
                      label={feature.status} 
                      size="small"
                      color={
                        feature.status === 'completed' ? 'success' :
                        feature.status === 'in-progress' ? 'info' :
                        'default'
                      }
                    />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" paragraph>
                    {feature.description}
                  </Typography>
                  
                  {feature.acceptance && feature.acceptance.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Acceptance Criteria
                      </Typography>
                      <List dense>
                        {feature.acceptance.map((criteria, i) => (
                          <ListItem key={i} dense>
                            <ListItemIcon sx={{ minWidth: 28 }}>
                              <DoneIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText primary={criteria} />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        );
      }
    } catch (e) {
      // If not JSON, render as markdown
    }
    
    return (
      <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 1 }}>
        <ReactMarkdown>{content}</ReactMarkdown>
      </Box>
    );
  };
  
  // Features prompt configuration
  const featuresPromptConfig = {
    system: "You are a product manager specializing in feature definition and prioritization.",
    examples: [
      "What key features should our e-commerce platform include?",
      "Define the MVP features for a social media application"
    ],
    instruction: "Create detailed feature definitions including descriptions, acceptance criteria, and priority levels."
  };
  
  // Handle feature selection
  const handleFeatureSelect = (feature) => {
    setSelectedFeature(feature);
    setEditMode(false);
    setEditedFeature(null);
  };
  
  // Handle editing a feature
  const handleEditFeature = () => {
    setEditMode(true);
    setEditedFeature({...selectedFeature});
  };
  
  // Handle saving edited feature
  const handleSaveFeature = async () => {
    if (!editedFeature) return;
    
    try {
      const updatedFeature = await updateFeature(editedFeature._id, editedFeature);
      
      // Update features list
      setFeatures(features.map(f => 
        f._id === updatedFeature._id ? updatedFeature : f
      ));
      
      // Update selected feature
      setSelectedFeature(updatedFeature);
      
      setEditMode(false);
      setEditedFeature(null);
      
      showNotification({
        message: 'Feature updated successfully',
        type: 'success'
      });
    } catch (error) {
      console.error('Error updating feature:', error);
      showNotification({
        message: `Error updating feature: ${error.message}`,
        type: 'error'
      });
    }
  };
  
  // Handle creating a new feature
  const handleCreateFeature = async () => {
    try {
      const newFeature = {
        title: 'New Feature',
        description: 'Feature description',
        priority: 'medium',
        status: 'pending',
        acceptance: ['Acceptance criteria 1']
      };
      
      const createdFeature = await createFeature(projectId, newFeature);
      
      // Add to features list
      setFeatures([...features, createdFeature]);
      
      // Select the new feature
      setSelectedFeature(createdFeature);
      setEditMode(true);
      setEditedFeature(createdFeature);
      
      showNotification({
        message: 'New feature created',
        type: 'success'
      });
    } catch (error) {
      console.error('Error creating feature:', error);
      showNotification({
        message: `Error creating feature: ${error.message}`,
        type: 'error'
      });
    }
  };
  
  // Handle deleting a feature
  const handleDeleteFeature = async () => {
    if (!selectedFeature) return;
    
    try {
      await deleteFeature(selectedFeature._id);
      
      // Remove from features list
      setFeatures(features.filter(f => f._id !== selectedFeature._id));
      
      // Clear selection
      setSelectedFeature(null);
      setEditMode(false);
      setEditedFeature(null);
      
      showNotification({
        message: 'Feature deleted',
        type: 'success'
      });
    } catch (error) {
      console.error('Error deleting feature:', error);
      showNotification({
        message: `Error deleting feature: ${error.message}`,
        type: 'error'
      });
    }
  };
  
  // Custom component for features editing
  const renderFeaturesEditor = () => {
    return (
      <Box>
        <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Features</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateFeature}
            >
              Add Feature
            </Button>
          </Box>
          <Divider sx={{ mb: 2 }} />
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 1, height: '500px', overflow: 'auto' }}>
                <Typography variant="subtitle2" gutterBottom>
                  Feature List
                </Typography>
                <List>
                  {features.map(feature => (
                    <ListItem 
                      key={feature._id}
                      button
                      selected={selectedFeature && selectedFeature._id === feature._id}
                      onClick={() => handleFeatureSelect(feature)}
                      sx={{ mb: 1, borderRadius: 1, bgcolor: 'background.paper' }}
                    >
                      <ListItemText 
                        primary={feature.title} 
                        secondary={
                          <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                            <Chip 
                              label={feature.priority} 
                              size="small"
                              color={
                                feature.priority === 'high' ? 'error' :
                                feature.priority === 'medium' ? 'warning' :
                                'default'
                              }
                            />
                            <Chip 
                              label={feature.status} 
                              size="small"
                              color={
                                feature.status === 'completed' ? 'success' :
                                feature.status === 'in-progress' ? 'info' :
                                'default'
                              }
                            />
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={8}>
              <Box sx={{ height: '500px', overflow: 'auto' }}>
                {selectedFeature ? (
                  <Box>
                    {editMode ? (
                      // Edit mode
                      <Box>
                        <TextField
                          fullWidth
                          label="Feature Title"
                          value={editedFeature.title}
                          onChange={(e) => setEditedFeature({...editedFeature, title: e.target.value})}
                          margin="normal"
                        />
                        
                        <TextField
                          fullWidth
                          label="Description"
                          value={editedFeature.description}
                          onChange={(e) => setEditedFeature({...editedFeature, description: e.target.value})}
                          multiline
                          rows={4}
                          margin="normal"
                        />
                        
                        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                          <TextField
                            select
                            label="Priority"
                            value={editedFeature.priority}
                            onChange={(e) => setEditedFeature({...editedFeature, priority: e.target.value})}
                            SelectProps={{
                              native: true,
                            }}
                            sx={{ width: '50%' }}
                          >
                            <option value="high">High</option>
                            <option value="medium">Medium</option>
                            <option value="low">Low</option>
                          </TextField>
                          
                          <TextField
                            select
                            label="Status"
                            value={editedFeature.status}
                            onChange={(e) => setEditedFeature({...editedFeature, status: e.target.value})}
                            SelectProps={{
                              native: true,
                            }}
                            sx={{ width: '50%' }}
                          >
                            <option value="pending">Pending</option>
                            <option value="in-progress">In Progress</option>
                            <option value="completed">Completed</option>
                          </TextField>
                        </Box>
                        
                        <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
                          Acceptance Criteria
                        </Typography>
                        
                        {editedFeature.acceptance && editedFeature.acceptance.map((criteria, index) => (
                          <Box key={index} sx={{ display: 'flex', mb: 1 }}>
                            <TextField
                              fullWidth
                              value={criteria}
                              onChange={(e) => {
                                const newAcceptance = [...editedFeature.acceptance];
                                newAcceptance[index] = e.target.value;
                                setEditedFeature({...editedFeature, acceptance: newAcceptance});
                              }}
                              size="small"
                            />
                            <IconButton 
                              color="error"
                              onClick={() => {
                                const newAcceptance = editedFeature.acceptance.filter((_, i) => i !== index);
                                setEditedFeature({...editedFeature, acceptance: newAcceptance});
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        ))}
                        
                        <Button
                          variant="outlined"
                          startIcon={<AddIcon />}
                          onClick={() => {
                            const newAcceptance = [...(editedFeature.acceptance || []), ''];
                            setEditedFeature({...editedFeature, acceptance: newAcceptance});
                          }}
                          sx={{ mt: 1 }}
                        >
                          Add Criteria
                        </Button>
                        
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, gap: 1 }}>
                          <Button
                            variant="outlined"
                            onClick={() => {
                              setEditMode(false);
                              setEditedFeature(null);
                            }}
                          >
                            Cancel
                          </Button>
                          <Button
                            variant="contained"
                            onClick={handleSaveFeature}
                          >
                            Save Changes
                          </Button>
                        </Box>
                      </Box>
                    ) : (
                      // View mode
                      <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                          <Typography variant="h6">{selectedFeature.title}</Typography>
                          <Box>
                            <Button
                              variant="outlined"
                              startIcon={<EditIcon />}
                              onClick={handleEditFeature}
                              sx={{ mr: 1 }}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="outlined"
                              color="error"
                              startIcon={<DeleteIcon />}
                              onClick={handleDeleteFeature}
                            >
                              Delete
                            </Button>
                          </Box>
                        </Box>
                        
                        <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                          <Chip 
                            icon={<FlagIcon />}
                            label={`Priority: ${selectedFeature.priority}`} 
                            color={
                              selectedFeature.priority === 'high' ? 'error' :
                              selectedFeature.priority === 'medium' ? 'warning' :
                              'default'
                            }
                          />
                          <Chip 
                            icon={<TaskIcon />}
                            label={`Status: ${selectedFeature.status}`} 
                            color={
                              selectedFeature.status === 'completed' ? 'success' :
                              selectedFeature.status === 'in-progress' ? 'info' :
                              'default'
                            }
                          />
                        </Box>
                        
                        <Typography variant="subtitle1" gutterBottom>Description</Typography>
                        <Typography variant="body1" paragraph>
                          {selectedFeature.description}
                        </Typography>
                        
                        <Typography variant="subtitle1" gutterBottom>Acceptance Criteria</Typography>
                        {selectedFeature.acceptance && selectedFeature.acceptance.length > 0 ? (
                          <List dense>
                            {selectedFeature.acceptance.map((criteria, i) => (
                              <ListItem key={i} dense>
                                <ListItemIcon>
                                  <DoneIcon />
                                </ListItemIcon>
                                <ListItemText primary={criteria} />
                              </ListItem>
                            ))}
                          </List>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No acceptance criteria defined
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <FeaturesIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="body1" color="text.secondary">
                      Select a feature to view details
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={handleCreateFeature}
                      sx={{ mt: 2 }}
                    >
                      Create New Feature
                    </Button>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    );
  };
  
  return (
    <DocumentTypeTemplate
      projectId={projectId}
      documentType="features"
      title="Features"
      icon={FeaturesIcon}
      existingContent={existingContent}
      onSave={onSave}
      renderPreview={renderFeaturesPreview}
      promptConfig={featuresPromptConfig}
      allowedImportTypes="application/json,text/plain,text/markdown"
    >
      {/* Additional custom content - features editor */}
      {renderFeaturesEditor()}
    </DocumentTypeTemplate>
  );
};

export default Features;